import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, ArrowDownRight } from "lucide-react";
import { MaskedLines } from "./motion";

const FrameTag = ({ k, v, className }) => (
  <div className={`absolute rounded-xl border border-white/15 bg-ink/70 px-4 py-3 backdrop-blur-md ${className}`}>
    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">{k}</p>
    <p className="mt-1 font-display text-sm text-white">{v}</p>
  </div>
);

const HeroFrame = ({ imgY, rx, ry }) => (
  <div className="relative [perspective:1400px]">
    <motion.div
      style={{ rotateX: rx, rotateY: ry }}
      initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.4, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="relative [transform-style:preserve-3d]"
    >
      <div className="clip-cut relative h-[440px] overflow-hidden md:h-[560px] lg:h-[640px]">
        <motion.img style={{ y: imgY }} src="/images/tower.jpg" alt="Glass commercial office tower under a clear sky" className="absolute inset-0 h-[120%] w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/10 to-transparent" />
        <div className="spot absolute inset-0" />
        <div className="sweep absolute inset-0" aria-hidden="true" />
      </div>
      <FrameTag k="Scope" v="Documented, per area" className="left-4 top-6 md:-left-8 [transform:translateZ(60px)]" />
      <FrameTag k="Schedule" v="Recurring, set windows" className="bottom-24 right-4 md:-right-6 [transform:translateZ(80px)]" />
      <FrameTag k="Contact" v="One accountable manager" className="bottom-6 left-6 [transform:translateZ(40px)]" />
    </motion.div>
  </div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "10%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 110, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 110, damping: 18 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section ref={ref} onMouseMove={onMove} className="relative overflow-hidden bg-ink pb-20 pt-32 text-white md:pt-40" data-testid="hero-section">
      <div className="grain" aria-hidden="true" />
      <div className="glow-orb absolute -left-40 top-20 h-[520px] w-[520px] opacity-60" aria-hidden="true" />
      <div className="relative mx-auto grid max-w-[1400px] items-center gap-14 px-5 md:px-10 lg:grid-cols-12">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="font-mono text-xs uppercase tracking-[0.25em] text-glint">
            Commercial cleaning · West Valley, Arizona
          </motion.p>
          <h1 className="mt-8 font-display text-[11vw] font-semibold leading-[0.95] tracking-[-0.035em] sm:text-6xl lg:text-[4.6rem] xl:text-[5.4rem]" data-testid="hero-heading">
            <MaskedLines lines={["A cleaner workplace.", <span className="text-slate-300">A more dependable</span>, <>cleaning <span className="font-serif font-normal italic text-glint">partner.</span></>]} />
          </h1>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.9 }}>
            <p className="mt-10 max-w-xl text-base leading-relaxed text-slate-300 md:text-lg">
              Professional commercial cleaning for offices, retail spaces, healthcare facilities, and commercial properties throughout the Phoenix West Valley.
            </p>
            <div className="mt-10 flex flex-col gap-4 sm:flex-row">
              <Link to="/request-quote" data-testid="hero-quote-btn" className="btn-glint group !px-7 !py-4 text-base">
                Request a Free Cleaning Quote <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/services" data-testid="hero-explore-services-btn" className="btn-outline-light group">Explore Our Services <ArrowDownRight className="h-4 w-4 transition-transform group-hover:rotate-[-45deg]" /></Link>
            </div>
          </motion.div>
        </motion.div>
        <div className="lg:col-span-5"><HeroFrame imgY={imgY} rx={rx} ry={ry} /></div>
      </div>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 }} className="relative mx-auto mt-20 grid max-w-[1400px] grid-cols-1 gap-6 border-t border-white/10 px-5 pt-8 sm:grid-cols-3 md:px-10">
        {[["01", "Recurring contracts, not one-off jobs"], ["02", "Professionally managed accounts"], ["03", "Written scope for every facility"]].map(([n, t]) => (
          <p key={n} className="flex items-baseline gap-4 text-sm text-slate-300"><span className="font-mono text-xs text-glint">{n}</span>{t}</p>
        ))}
      </motion.div>
    </section>
  );
};
